import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { Constants } from 'app/shared/shared-constants';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { HttpServerLinkedService, httpAuthTypes, httpCertificateLocationTypes } from 'app/model/linkedservice/http-server.linkedservice';
import {
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase,
    passwordOrAKVtoSecretBase,
    secretBaseToPasswordOrAKV
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { KeyValuePairWithAkv, Owned } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HttpServerLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                url: '*',
                enableServerCertificateValidation: '*',
                authenticationType: '*',
                userName: '*',
                password: 'secret',
                embeddedCertData: 'secret',
                certThumbprint: '*',
                encryptedCredential: '*',
                /* use 'secret' instead of '*' here as `referAKVinNonCredentialProperties()` sets `fullyByJsonEditor` to `true` with '*' & AKV
                 * change is safe as this does not change the result of `checkLinkedServiceWithoutSecrets()`
                 * do NOT change to `secretRemainer`, otherwise `remainderWithExternalSpec()` will extract its value to raw
                 */
                authHeaders: 'secret'
            },
            parameters: '*'
        }
    }
})
export class HttpLinkedServiceAdapter extends LinkedServiceAdapter<HttpServerLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => HttpServerLinkedService {
        return HttpServerLinkedService;
    }

    protected model(): IModel {
        return HttpServerLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: HttpServerLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.HttpLinkedService;
        properties.type = Metamodel.modelFor(HttpServerLinkedService).name;
        let userName: string;
        let password: string | Model.SecretBase;
        let embeddedCertData: Model.SecureString;
        let certThumbprint: string;
        if (linkedService.authenticationType === httpAuthTypes.Basic || linkedService.authenticationType === httpAuthTypes.Digest
            || linkedService.authenticationType === httpAuthTypes.Windows) {
            userName = this.valueToResource(linkedService, HttpServerLinkedService.USER_NAME);
            password = parameterizablePasswordOrAKVtoSecretBase(linkedService, HttpServerLinkedService.PASSWORD, this.context);
        } else if (linkedService.authenticationType === httpAuthTypes.ClientCertificate) {
            if (linkedService.certificateLocationType === httpCertificateLocationTypes.CertificateThumbprint) {
                certThumbprint = this.valueToResource(linkedService, HttpServerLinkedService.CERTIFICATE_THUMBPRINT);
            } else if (linkedService.certificateLocationType === httpCertificateLocationTypes.EmbeddedCertificateData) {
                if (!stringIsNullOrEmpty(linkedService.embeddedCertData)) {
                    embeddedCertData = {
                        type: 'SecureString',
                        value: linkedService.embeddedCertData
                    };
                }
                password = parameterizablePasswordOrAKVtoSecretBase(linkedService, HttpServerLinkedService.PASSWORD, this.context);
            }
        }

        properties.typeProperties = {
            url: this.valueToResource(linkedService, HttpServerLinkedService.BASE_URL),
            enableServerCertificateValidation: linkedService.enableServerCertificateValidation,
            authenticationType: linkedService.authenticationType,
            userName,
            password,
            embeddedCertData,
            certThumbprint
        };

        if (!arrayIsNullOrEmpty(linkedService.authHeaders)) {
            properties.typeProperties.authHeaders = {};
            linkedService.authHeaders.forEach((pair) => {
                if (pair.value != null) {
                    properties.typeProperties.authHeaders[pair.key] = passwordOrAKVtoSecretBase(pair.value, this.context);
                }
            });
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: HttpServerLinkedService): Promise<HttpServerLinkedService> {
        const properties = linkedServiceResource.properties as Model.HttpLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.HttpLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, HttpServerLinkedService.BASE_URL, typeProperties.url);
        ValueAdapter.toModel(linkedService, HttpServerLinkedService.USER_NAME, typeProperties.userName);

        linkedService.enableServerCertificateValidation = typeProperties.enableServerCertificateValidation;
        linkedService.authenticationType = typeProperties.authenticationType;

        if (typeProperties.certThumbprint) {
            linkedService.certificateLocationType = httpCertificateLocationTypes.CertificateThumbprint;
            ValueAdapter.toModel(linkedService, HttpServerLinkedService.CERTIFICATE_THUMBPRINT, typeProperties.certThumbprint);
        } else {
            linkedService.certificateLocationType = httpCertificateLocationTypes.EmbeddedCertificateData;
            linkedService.embeddedCertData = Constants.maskedCredential;
        }
        parameterizableSecretBaseToModel(linkedService, HttpServerLinkedService.PASSWORD, typeProperties.password, this.context,
            linkedService.authenticationType === httpAuthTypes.Basic || linkedService.authenticationType === httpAuthTypes.Digest || linkedService.authenticationType === httpAuthTypes.Windows
            || (linkedService.authenticationType === httpAuthTypes.ClientCertificate && linkedService.certificateLocationType === httpCertificateLocationTypes.EmbeddedCertificateData));

        if (typeProperties.authHeaders) {
            linkedService.authHeaders = [];
            Object.keys(typeProperties.authHeaders).forEach((key) => {
                if (typeProperties.authHeaders[key] != null) {
                    const kv = Owned.create<KeyValuePairWithAkv>(KeyValuePairWithAkv.MODEL, linkedService);
                    kv.key = key;
                    kv.value = secretBaseToPasswordOrAKV((typeProperties.authHeaders[key]), true, this.context);
                    linkedService.authHeaders.push(kv);
                }
            });
        }
        return linkedService;
    }
}
